import { GQL } from '@aline/graphql';
import type { BaseEditor, Editor, Node, Text, Transforms } from 'slate';

import type { UUID } from '../../../types/type-helpers';
import type { getPresenceColor } from '../../../util';
import type {
  AlineSelection,
  AlineTextFormats,
  AlineTransforms,
  DocParts,
  isRenderModelLine,
  isRenderModelPage,
  measureText,
  Queries,
  RenderModelDocument,
  RenderModelFootersElement,
  RenderModelHeadersElement,
  SelEventSource,
} from '../../internal';
import type {
  isCheckboxElement,
  isCheckboxOptionFieldElement,
  isInputFieldElement,
  isParagraphElement,
} from '../type-guards';

export type AlineMarks = Partial<AlineTextFormats> | null;
export type ExpandedEmptyBlocks = Set<UUID>;

/**
 * State that is relevant for an Aline user's use of an Editor object.
 */
interface AlineUserState {
  editingUserId: UUID | null;
  isInSigningCeremony: boolean;
  /**
   * Can occur for a number of reasons, for example:
   * 1. Document is signed.
   * 2. Document is part of an in-progress DS Envelope.
   * 3. The Document is in a signing ceremony and the User is not a signer, the User's signing step is not triggered, or has already signed.
   */
  isReadOnly: boolean;
  isSigned: boolean;
  // Set<nodeId> indicating whether the empty block w/ that nodeId is expanded.  By default, empty blocks are collapsed.
  expandedEmptyBlocks?: ExpandedEmptyBlocks;
  userDeclinedSigning?: boolean;
}

export type TransformName = keyof typeof AlineTransforms;

export interface DocumentInfo {
  id: UUID;
  latestVersion: {
    __typename: 'DocumentVersion';
    id: string;
  };
  name: string;
  variant: GQL.DocumentVariant;
}

/**
 * IMPORTANT: When adding new properties, copy them onto the RenderModelEditor manually in
 * Queries.buildRenderModelEditor. TypeScript WILL NOT throw an error about missing properties when
 * building the RenderModelEditor, including required properties.
 */
export interface RenderModelEditor extends BaseEditor {
  children: [RenderModelDocument, RenderModelHeadersElement, RenderModelFootersElement];
  selection: AlineSelection | null;
  /** Announce a message to the screen reader. */
  announce?: (message: string) => void;
  documentInfo?: DocumentInfo;
  measureText?: typeof measureText;
  renderModel?: RenderModelDocument;
  type?: 'AlineEditor';
  userState?: AlineUserState;
}
export interface AlineEditor extends BaseEditor {
  children: DocParts;
  selection: AlineSelection | null;
  /** Announce a message to the screen reader. */
  announce?: (message: string) => void;
  documentInfo?: DocumentInfo;
  measureText?: typeof measureText;
  renderModel?: RenderModelDocument;
  type?: 'AlineEditor';
  userState?: AlineUserState;
}

export enum EditingMode {
  COMMENTING = 'COMMENTING',
  EDITING = 'EDITING',
  SUGGESTING = 'SUGGESTING',
  VIEWING = 'VIEWING',
}

export enum Owner {
  Owner = 'OWNER',
}

// Extending enums is weird.  Let's give it a crack.  Motivation - https://github.com/microsoft/TypeScript/issues/17592#issuecomment-449440944.
export type PermissionRole = GQL.PermissionRole | Owner;
export const PermissionRole = { ...GQL.PermissionRole, ...Owner };
export type WorkflowAccessLevel = GQL.WorkflowAccessLevel | Owner;
export const WorkflowAccessLevel = { ...GQL.WorkflowAccessLevel, ...Owner };

export const orgAdminDefaultDocumentRole = PermissionRole.Editor;
export const communityTemplateDefaultRole = PermissionRole.Viewer;
export const orgAdminDefaultWorkflowAccessLevel = WorkflowAccessLevel.Assignee;
export const communityWorkflowDefaultAccessLevel = WorkflowAccessLevel.Viewer;

export interface PlaywrightAdapter {
  AlineTransforms: typeof AlineTransforms;
  editor: AlineEditor;
  Editor: typeof Editor;
  getPresenceColor: typeof getPresenceColor;
  isCheckboxElement: typeof isCheckboxElement;
  isCheckboxOptionFieldElement: typeof isCheckboxOptionFieldElement;
  isInputFieldElement: typeof isInputFieldElement;
  isParagraphElement: typeof isParagraphElement;
  isRenderModelLine: typeof isRenderModelLine;
  isRenderModelPage: typeof isRenderModelPage;
  Node: typeof Node;
  Queries: typeof Queries;
  SelEventSource: typeof SelEventSource;
  Text: typeof Text;
  Transforms: typeof Transforms;
}
